import type {
  ClassroomCourse,
  ClassroomSyncRequest,
  ClassroomSyncResponse,
} from '@study-decks/shared'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''

export class ClassroomApiError extends Error {
  override readonly name = 'ClassroomApiError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

function friendlyMessage(code: string, fallback: string): string {
  if (code === 'CLASSROOM_RECONSENT_REQUIRED') return 'Reconnect Google Classroom to continue.'
  if (code === 'MISSING_REFRESH_TOKEN') {
    return "Google didn't return a refresh token. Try reconnecting."
  }
  if (code === 'EMAIL_MISMATCH') {
    return "The Google account you connected doesn't match your sign-in. Sign out, sign in with the right account, then reconnect."
  }
  return fallback
}

async function requestClassroom<T>(path: string, idToken: string, init: RequestInit = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${idToken}`,
      ...init.headers,
    },
  })
  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string }
    }
    const code = errorBody.error?.code ?? 'REQUEST_FAILED'
    throw new ClassroomApiError(
      code,
      friendlyMessage(code, errorBody.error?.message ?? `Request failed (${response.status})`),
    )
  }
  return (await response.json()) as T
}

export async function listClassroomCourses(idToken: string): Promise<ClassroomCourse[]> {
  const response = await requestClassroom<{ courses: ClassroomCourse[] }>(
    '/api/classroom/courses',
    idToken,
  )
  return response.courses
}

export async function syncClassroomCourse(
  idToken: string,
  googleCourseId: string,
  body: ClassroomSyncRequest,
): Promise<ClassroomSyncResponse> {
  return requestClassroom<ClassroomSyncResponse>(
    `/api/classroom/sync/${encodeURIComponent(googleCourseId)}`,
    idToken,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
  )
}
